import React, { useState, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { downloadPDF } from '../../utils/pdfUtils';

// Register a custom font (optional)
Font.register({
  family: 'Roboto',
  src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf'
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Roboto',
  },
  headerContainer: {
    marginBottom: 30,
    borderBottom: 2,
    borderBottomColor: '#333',
    paddingBottom: 10,
  },
  businessHeader: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 10,
    color: '#1a365d',
  },
  businessInfo: {
    fontSize: 10,
    textAlign: 'center',
    color: '#4a5568',
    marginBottom: 5,
  },
  receiptTitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  customerSection: {
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#f7fafc',
  },
  customerInfo: {
    fontSize: 11,
    marginBottom: 5,
  },
  table: {
    display: 'table',
    width: 'auto',
    marginVertical: 10,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#edf2f7',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    minHeight: 25,
    alignItems: 'center',
  },
  tableCell: {
    padding: 5,
    fontSize: 10,
  },
  totalsSection: {
    marginTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 10,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
    fontSize: 11,
  },
  grandTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    marginTop: 5,
    fontSize: 12,
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#718096',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 10,
  },
});

export default function OrderReceipt({ order, customerData, businessDetails }) {
  const PDFContent = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerContainer}>
          <Text style={styles.businessHeader}>{businessDetails?.name || 'Business Name'}</Text>
          <Text style={styles.businessInfo}>{businessDetails?.address || 'Business Address'}</Text>
          <Text style={styles.businessInfo}>
            Phone: {businessDetails?.phone || 'N/A'} | Email: {businessDetails?.email || 'N/A'}
          </Text>
          <Text style={styles.businessInfo}>GST No: {businessDetails?.gstNumber || 'N/A'}</Text>
        </View>

        <Text style={styles.receiptTitle}>Sales Receipt</Text>

        <View style={styles.customerSection}>
          <Text style={styles.customerInfo}>Bill To:</Text>
          <Text style={styles.customerInfo}>Name: {order.customerName}</Text>
          <Text style={styles.customerInfo}>Address: {customerData?.address || 'N/A'}</Text>
          <Text style={styles.customerInfo}>Phone: {customerData?.phone || 'N/A'}</Text>
          <Text style={styles.customerInfo}>
            Date: {new Date(order.finalizedAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </Text>
          <Text style={styles.customerInfo}>Invoice No: INV-{order.id}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCell, { flex: 2 }]}>Item</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>Quantity</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>Price/kg</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>Amount</Text>
          </View>

          {order.items.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, { flex: 2 }]}>{item.vegetable}</Text>
              <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                {item.actualQuantity} {item.unit}
              </Text>
              <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                Rs. {Number(item.pricePerKg).toFixed(2)}
              </Text>
              <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>
                Rs. {(Number(item.actualQuantity) * Number(item.pricePerKg)).toFixed(2)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text>Subtotal:</Text>
            <Text>Rs. {Number(order.subtotal).toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Discount:</Text>
            <Text>Rs. {Number(order.discount).toFixed(2)}</Text>
          </View>
          <View style={styles.grandTotal}>
            <Text>Total Amount:</Text>
            <Text>Rs. {Number(order.totalAmount).toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Amount Received:</Text>
            <Text>Rs. {Number(order.amountReceived).toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Balance:</Text>
            <Text>Rs. {Math.abs(Number(order.remainingBalance)).toFixed(2)}</Text>
          </View>
        </View>

        <Text style={styles.footer}>
          Thank you for your business! For any queries, please contact us at {businessDetails?.phone || 'N/A'}
        </Text>
      </Page>
    </Document>
  );

  return (
    <button
      onClick={async () => {
        const doc = <PDFContent />;
        await downloadPDF(doc, `Invoice-${order.id}.pdf`);
      }}
      style={{
        textDecoration: 'none',
        // Add any other styles you need
      }}
    >
      Download PDF
    </button>
  );
}